import { Children, isValidElement, type CSSProperties, type ReactNode } from "react";

export type SelectOption = { value: string; label: string; disabled: boolean; group?: string; attention?: boolean };
const plain = (node: ReactNode): string => Children.toArray(node).map(child => isValidElement<{ children?: ReactNode }>(child) ? plain(child.props.children) : String(child)).join("");

/** The native form value is the identity; duplicate values cannot name different choices. */
export function selectOptions(children: ReactNode): SelectOption[] {
  const options = new Map<string, SelectOption>();
  const visit = (nodes: ReactNode, disabled = false, group?: string) => {
    Children.forEach(nodes, child => {
      if (!isValidElement<{ value?: string | number; label?: string; children?: ReactNode; disabled?: boolean; "data-attention"?: boolean }>(child)) return;
      const inactive = disabled || !!child.props.disabled;
      if (child.type === "option") {
        const value = String(child.props.value ?? plain(child.props.children));
        if (!options.has(value)) options.set(value, { value, label: child.props.label ?? plain(child.props.children), disabled: inactive, group, ...(child.props["data-attention"] ? { attention: true } : {}) });
      } else visit(child.props.children, inactive, child.type === "optgroup" ? child.props.label : group);
    });
  };
  visit(children); return [...options.values()];
}

export const filterSelectOptions = (options: SelectOption[], query: string) => {
  const normalized = query.trim().normalize("NFKC").toLocaleLowerCase();
  return options.filter(option => option.label.normalize("NFKC").toLocaleLowerCase().includes(normalized));
};
export function nextSelectValue(options: SelectOption[], current: string | null, key: string): string | null {
  const enabled = options.filter(option => !option.disabled);
  if (!enabled.length) return null;
  if (key === "Home") return enabled[0].value;
  if (key === "End") return enabled.at(-1)!.value;
  const index = enabled.findIndex(option => option.value === current);
  if (index < 0) return (key === "ArrowUp" ? enabled.at(-1)! : enabled[0]).value;
  return enabled[(index + (key === "ArrowUp" ? -1 : 1) + enabled.length) % enabled.length].value;
}

export type SelectViewport = { left: number; top: number; width: number; height: number; layoutHeight: number };
/** Layout-viewport coordinates, including a panned/resized visual viewport. */
export function selectPopupPosition(anchor: Pick<DOMRect, "left" | "top" | "bottom" | "width">, viewport: SelectViewport, adaptToContent = true, minFloor = 160): CSSProperties {
  const inset = 8, gap = 6;
  /* 2026-10-16: 폭을 280 으로 못 박던 것을 걷었다. claude.ai/code 의 메뉴는 **내용에 맞춰**
     줄어든다 — "일 / 주 / 2주" 여섯 줄짜리 메뉴가 280 을 차지하면, 고른 것 옆에 아무것도
     없는 빈 자리가 글자보다 넓어 메뉴가 아니라 빈 판으로 읽혔다.
     아래 값은 **최소** 폭이다(고르개보다 좁아지지는 않는다). 실제 폭은 내용이 정하고,
     화면 밖으로 나가지 않도록 오른쪽 끝까지로만 늘어난다. */
  /* minFloor: 설정 창 안에서는 0 — claude.ai 설정의 목록은 고르개 폭 이상에서 내용만큼 준다(84 고르개에 112).
     rail-reference.json `settingsRow.menu`. 다른 자리는 예전처럼 160 이 바닥이다. */
  const minWidth = Math.max(0, Math.min(viewport.width - inset * 2, Math.max(minFloor, anchor.width)));
  const width = minWidth;
  const left = Math.max(viewport.left + inset, Math.min(anchor.left, viewport.left + viewport.width - width - inset));
  const top = viewport.top + inset, bottom = viewport.top + viewport.height - inset;
  const start = Math.max(top, Math.min(anchor.bottom + gap, bottom));
  const end = Math.max(top, Math.min(anchor.top - gap, bottom));
  const above = end - top, below = bottom - start;
  // Prefer below, but never leave only a search box and a zero-height option
  // list. Use the space above, or the visual viewport on very short screens.
  if (below <= 0) return { left, top: viewport.top + inset, minWidth, maxWidth: Math.max(0, viewport.left + viewport.width - inset - left), maxHeight: Math.max(0, Math.min(380, viewport.height - inset * 2)) };
  if (adaptToContent && below < 160) {
    if (above >= 160 && above > below) return { left, bottom: viewport.layoutHeight - end, minWidth, maxWidth: Math.max(0, viewport.left + viewport.width - inset - left), maxHeight: Math.min(380, above) };
    return { left, top, minWidth, maxWidth: Math.max(0, viewport.left + viewport.width - inset - left), maxHeight: Math.max(0, Math.min(380, viewport.height - inset * 2)) };
  }
  return { left, minWidth, maxWidth: Math.max(0, viewport.left + viewport.width - inset - left), top: start, bottom: undefined, maxHeight: Math.max(0, Math.min(380, below)) };
}
